import { useContext, useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { CircularProgress } from "@mui/material";
import * as _ from "lodash";
import ChartDataLabels from "chartjs-plugin-datalabels";

import "./Dashboard.scss";

import { AbsDropDown, LoadingMessage } from "abs-ui-components";

import {
	DASHBOARD_CONSTANTS,
	CHILD_REPORTS_CHART_DATA,
} from "../../constants/DashboardCardConstants";
import read from "../../images/read.png";
import unread from "../../images/unread.png";
import AbsCard from "../../common/AbsCard/AbsCard";

import { getDashboard } from "../../services/DashboardService";
import { APP_TITLE } from "../../common/header/constants";
import { use } from "react";
import { ClaimsContext } from "../../services/ClaimsService";
import { FeatureType } from "../../constants/FeatureToggles";

ChartJS.register(ArcElement, ChartDataLabels);
ChartJS.register(Tooltip, Legend);

const DashboardPage = () => {
	const [year, setYear] = useState(new Date().getFullYear());
	const [notificationsData, setNotificationsData] = useState([]);
	const [dashboardConstants, setDashboardConstants] =
		useState(DASHBOARD_CONSTANTS);
	const [childReportsChartData, setChildReportsChartData] = useState(
		CHILD_REPORTS_CHART_DATA
	);
	const [yearlyReportCount, setYearlyReportCount] = useState([]);
	const [loader, setLoader] = useState(true);
	const args = {
		message: "Chart is Loading",
		showDots: true,
	};
    const {userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
    const isLegacyReportsEnabled =
			userFeaturesAreLoaded && userHasFeature(FeatureType.LegacyReports);
	const options = {
		responsive: true,
		plugins: {
			datalabels: {
				color: "#ffff",
				display: (context) => context?.dataset?.data[context?.dataIndex] > 0,
				font: {
					size: 16,
					weight: "bold",
				},
			},
			legend: {
				position: "bottom",
			},
		},
	};
	let isAllZeros =
		childReportsChartData?.datasets?.[0]?.data?.every(
			(value) => value === 0
		) ?? false;

	if (isAllZeros) {
		if (options?.plugins?.legend?.position) {
			options.plugins.legend = {
				position: "top",
				labels: {
					color: "#3e5162",
					font: {
						size: 14,
						weight: "bold",
					},
					boxWidth: 10,
					boxHeight: 10,
					usePointStyle: true,
				},
				onClick: (e, legendItem, legend) => {},
			};
		}

		childReportsChartData.labels = ["No Charts Avaialble"];
	} else {
		childReportsChartData.labels = ["DCS", "EU MRV", "UK MRV", "FEU", "UK ETS"];
	}

	const pieChartData = loader ? (
		<div style={{ width: "150px", color: "#002a4e" }}>
			<LoadingMessage {...args} center={false} />
		</div>
	) : isAllZeros ? (
		<>
			<div className style={{ height: "135px" }}>
				<span>
					<Doughnut data={childReportsChartData} options={options} />
				</span>
			</div>
		</>
	) : (
		<>
			<div style={{ height: "auto" }}>
				<span>
					<Doughnut data={childReportsChartData} options={options} />
				</span>
			</div>
		</>
	);

	useEffect(() => {
		const fetchDashboardData = async () => {
			setLoader(true);
			try {
				const data = await getDashboard();

				setDashboardConstants((prevState) => ({
					...prevState,
					enrollVesselCard: {
						...prevState.enrollVesselCard,
						totalEnrolledVesselsCount:
							data?.EnrolledVesselsCount ?? 0,
						totalActiveVesselsCount: 0,
					},
					portCodeCard: {
						...prevState.portCodeCard,
						totalCount: data?.PortCodesCount,
					},
				}));

				setYearlyReportCount(data?.DashboardReportYears);

				const currentYearData = data?.DashboardReportYears.find(
					(value) => value.ReportYear === year
				);
				localStorage.setItem("selectedYear", year);
				 sessionStorage.removeItem("cerSelectedYear");


				setDashboardConstants((prevState) => ({
					...prevState,
					reportCard: {
						...prevState.reportCard,
						totalCount: currentYearData?.TotalReportsCount ?? 0,
					},
				}));

				setChildReportsChartData((prevState) => ({
					...prevState,
					datasets: prevState.datasets.map((dataset) => ({
						...dataset,
						data: [
							currentYearData?.DcsReportsCount ?? 0,
							currentYearData?.EuMrvReportsCount ?? 0,
							currentYearData?.UkMrvReportsCount ?? 0,
							currentYearData?.FeuReportsCount ?? 0,
							currentYearData?.UkEtsReportsCount ?? 0,
						],
					})),
				}));

				setNotificationsData(
					_.map(data?.NotificationMsts, (x) => ({
						message: x.NotificationMessage,
						isRead: x.IsRead,
					}))
				);
			} catch (error) {
				console.error("Error fetching dashboard data", error);
			}
			setLoader(false);
		};
		setTimeout(function () {
			fetchDashboardData();
		}, 1000);
	}, []);

	const getYearOptions = () => {
		const currentYear = new Date().getFullYear(); // Get the current year
		const startYear = 2018; // Define the starting year
		const options = [];
        const hideStart = 2018;
	    const hideEnd = 2022; 

		for (let year = currentYear + 1; year >= startYear; year--) {
			if (isLegacyReportsEnabled && year >= hideStart && year <= hideEnd) {
			continue;
		}
			options.push({ label: year, value: year });
		}

		return options;
	};

	const handleYearChange = (selectedYear) => {
		setYear(selectedYear);
		localStorage.setItem("selectedYear", selectedYear);
		const yearData = _.findLast(
			yearlyReportCount,
			(x) => x.ReportYear == selectedYear
		);
		let countData = [0, 0, 0, 0, 0];
		let reportCount = 0;

		if (yearData) {
			reportCount = yearData.TotalReportsCount;
			countData = [
				yearData?.DcsReportsCount ?? 0,
				yearData?.EuMrvReportsCount ?? 0,
				yearData?.UkMrvReportsCount ?? 0,
				yearData?.FeuReportsCount ?? 0,
				yearData?.UkEtsReportsCount ?? 0,
			];
		}

		setDashboardConstants((prevState) => ({
			...prevState,
			reportCard: {
				...prevState.reportCard,
				totalCount: reportCount,
			},
		}));

		setChildReportsChartData((prevState) => ({
			...prevState,
			datasets: prevState.datasets.map((dataset) => ({
				...dataset,
				data: countData,
			})),
		}));
	};

	return (
		<div className="page-container row">
			<div className="col-md-6">
				<div className="d-flex justify-content-between erp-dashboard">
					<div className="emmision-reporter-heading">
						{APP_TITLE} Dashboard
					</div>
				</div>
				<div className="d-flex justify-content-left erp-cards card-container-div row">
					<div className="collapsible-card-div col-md-6">
						<AbsCard
							cardData={dashboardConstants.enrollVesselCard}
							loader={loader}
						/>
					</div>
					<div className="collapsible-card-div col-md-6">
						<AbsCard
							cardData={dashboardConstants.portCodeCard}
							loader={loader}
						/>
					</div>
				</div>
			</div>
			<div className="col-md-6">
				<div
					// style={{ padding: "30px 35px" }}
					className="d-flex justify-content-left aev-with-drodwn"
				>
					<div className="emmision-reporter-heading letter-spce col-md-7">
						Annual Emissions Verification
					</div>
					<div className="choose-year year-width col-md-5">
						<div className="choose-year-label">Choose Year</div>
						<AbsDropDown
							name="Choose year"
							handleChange={(e) => handleYearChange(e.value)}
							selected={year}
							options={getYearOptions()}
						/>
					</div>
				</div>
				<div className="d-flex justify-content-left card-container-div aev-cards">
					<div className="collapsible-card-div report-crd col-md-6">
						<AbsCard
							className="abs-card"
							cardData={dashboardConstants.reportCard}
							loader={loader}
						/>
					</div>
					<div className="collapsible-card-div col-md-6">
						{/* <Card data={{ body: pieChartData }} /> */}
						<AbsCard isChartCard={true} chartData={pieChartData} />
					</div>
				</div>
			</div>

			<div className="col-md-9">
				<div className="d-flex justify-content-left align-items-center mt-5 emmision-reporter-heading pl-4 letter-spce">
					Notifications{" "}
					<span style={{ paddingLeft: "20px" }}>
						{loader ? <CircularProgress size={20} /> : ""}
					</span>
				</div>

				{notificationsData.length === 0 && !loader ? (
					<div className="m-4 pl-2 letter-spce">No notifications</div>
				) : (
					notificationsData.map((notification, index) => (
						<div className="m-4 pl-2" key={`notifi-${index}`}>
							<img
								className="mr-3"
								src={notification.isRead ? read : unread}
								alt={notification.isRead ? "Read" : "Unread"}
							/>
							<span
								style={{
									fontWeight: notification.isRead
										? "initial"
										: "700",
								}}
							>
								{notification.message}
							</span>
						</div>
					))
				)}

				{/* {notificationsData.length !== 0 ? (
					<div className="d-flex justify-content-left align-items-center pb-4 pl-4">
						<Button>View All Notifications</Button>
					</div>
				) : (
					<div></div>
				)} */}
			</div>
		</div>
	);
};

export default DashboardPage;
